import React, { useRef, useState } from "react";
import { composeUrl } from "../../utils/api";
import { useAuth } from "react-oidc-context";
import { getClaimValue, hasRoleClaim } from "../../utils/authUser";
import LimeClaimTypes from "../../models/limeClaimTypes";
import { Tabs, Tab, TabContent } from "../Shared/Tabs";
import { useNavigate } from "react-router-dom";
import useInterval from "../Shared/Hooks/useInterval";
import WellKnownRoles from "../../models/wellKnownRoles";
import MemberBulkUpload from "./MemberBulkUpload";
import MemberSingleUpload from "./MemberSingleUpload";
import MemberSearch from "./MemberSearch";
import refreshIcon from "../../images/refresh.svg";
import checkCircle from "../../images/check-circle.svg";
import xCircle from "../../images/x-circle.svg";
import alertCircle from "../../images/alert-circle.svg";
import {
    portalPageSubtitle,
    portalPageTitle,
    portalTableBodyCell,
    portalTableBodyRow,
    portalTableCard,
    portalTableHeadCell,
} from "../../theme/portalLayout";

export enum MemberFileStatusId {
    New = 1,
    Processing,
    Review,
    Confirming,
    Confirmed,
    Abandoned,
    Error
}

interface MemberFile {
    id: number;
    filename: string;
    statusId: MemberFileStatusId;
    statusName: string;
    uploadedOn: Date;
    reviewedOn: Date;
    new: number;
    updated: number;
    removed: number;
    unchanged: number;
}

function Members() {
    const auth = useAuth();
    const navigate = useNavigate();
    const inputFile = useRef<HTMLInputElement | null>(null);

    // State for uploading / file handling
    const [isEmptyFile, setIsEmptyFile] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);
    const [uploadDisabled, setUploadDisabled] = useState<boolean>(false);
    const [error, setError] = useState("");

    // Table/data states
    const [uploadedFiles, setUploadedFiles] = useState<MemberFile[]>([]);
    const [tablePage, setTablePage] = useState<number>(1);
    const [maxTablePage, setMaxTablePage] = useState<number>(1);

    // Auto-refresh states
    const [intervalDelay] = useState(5000);
    const [shouldAutoRefresh, setShouldAutoRefresh] = useState(false);
    const [activeTabKey, setActiveTabKey] = useState("1");

    const paginationLimit = 10;
    const processingStatuses: MemberFileStatusId[] = [
        MemberFileStatusId.New,
        MemberFileStatusId.Processing,
    ];

    const hasReviewFile = uploadedFiles.some(
        (file) => file.statusId === MemberFileStatusId.Review
    );

    const secondTabTitle = hasReviewFile
        ? "Previous Files & Changes (1 in review)"
        : "Uploaded Files & Changes";

    useInterval(() => refresh(), shouldAutoRefresh ? intervalDelay : null);

    React.useEffect(() => {
        if (!hasRoleClaim(auth.user, WellKnownRoles.OrganisationMemberManager)) {
            navigate("/access-denied");
        }
        refresh();
    }, []);

    function refresh() {
        setError("");
        const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
        fetch(composeUrl(`organisations/${orgUuid}/memberFiles`), {
            headers: [["Authorization", `Bearer ${auth.user?.access_token}`]],
            method: "GET",
        })
            .then(async (response) => {
                const payload = await response.json().catch(() => null);
                if (!response.ok) {
                    const message =
                        payload && typeof payload === "object" && "message" in payload
                            ? String((payload as { message?: string }).message)
                            : "Unable to load member files";
                    setError(message);
                    setUploadedFiles([]);
                    setShouldAutoRefresh(false);
                    setUploadDisabled(false);
                    return;
                }
                if (!Array.isArray(payload)) {
                    setUploadedFiles([]);
                    setShouldAutoRefresh(false);
                    setUploadDisabled(false);
                    return;
                }
                const data = payload as MemberFile[];
                const sortedUploads = [...data].sort(
                    (a, b) =>
                        new Date(b.uploadedOn).valueOf() - new Date(a.uploadedOn).valueOf()
                );
                if (sortedUploads.length > 0) {
                    switch (sortedUploads[0].statusId) {
                        case MemberFileStatusId.New:
                        case MemberFileStatusId.Processing:
                        case MemberFileStatusId.Review:
                        case MemberFileStatusId.Confirming:
                            setUploadDisabled(true);
                            break;
                        case MemberFileStatusId.Error:
                        case MemberFileStatusId.Abandoned:
                        case MemberFileStatusId.Confirmed:
                            setUploadDisabled(false);
                            break;
                    }
                    setTablePagination(sortedUploads);
                    setUploadedFiles(sortedUploads);
                } else {
                    setUploadDisabled(false);
                }

                setShouldAutoRefresh(
                    data.filter(
                        (f) =>
                            f.statusId === MemberFileStatusId.New ||
                            f.statusId === MemberFileStatusId.Processing ||
                            f.statusId === MemberFileStatusId.Confirming
                    ).length > 0
                );
            })
            .catch(() => {
                setError("Unable to load member files");
                setUploadedFiles([]);
                setShouldAutoRefresh(false);
                setUploadDisabled(false);
            });
    }

    function setTablePagination(memberFiles: MemberFile[]) {
        const maxPage = Math.ceil(memberFiles.length / paginationLimit);
        setMaxTablePage(maxPage);
    }
    
    async function handleUpload(file: File) {
        setError("");
        setIsEmptyFile(false);

        if (file.size === 0) {
            setIsEmptyFile(true);
            return;
        }

        setUploading(true);
        setUploadDisabled(true);

        try {
            const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
            const formData = new FormData();
            formData.append("file", file, file.name);

            const response = await fetch(composeUrl(`organisations/${orgUuid}/memberFiles`), {
                headers: [["Authorization", `Bearer ${auth.user?.access_token}`]],
                method: "POST",
                body: formData,
            });

            if (response.ok) {
                refresh();
                setActiveTabKey("2");
            } else {
                const responseBody = await response.json();
                if (responseBody.code && responseBody.message) {
                    setError(responseBody.message);
                } else {
                    setError("Sorry an error has occurred");
                }
            }
        } catch (e) {
            setError("Sorry an error has occurred");
        }

        setUploading(false);
        setUploadDisabled(false);
    }

    function getStatusElement(memberFile: MemberFile): JSX.Element {
        let icon = undefined;
        let iconAlt = "";
        let additionalClasses = "";

        switch (memberFile.statusId) {
            case MemberFileStatusId.New:
            case MemberFileStatusId.Processing:
            case MemberFileStatusId.Confirming:
                icon = refreshIcon;
                iconAlt = "Processing";
                additionalClasses = 'animate-spin';
                break;
            case MemberFileStatusId.Confirmed:
                icon = checkCircle;
                iconAlt = "Confirmed";
                break;
            case MemberFileStatusId.Error:
                icon = alertCircle;
                iconAlt = "Error";
                break;
            case MemberFileStatusId.Abandoned:
                icon = xCircle;
                iconAlt = "Abandoned";
                break;
            default:
                break;
        }

        return (
            <div className="leading-8 flex flex-row items-center">
                {icon && <img src={icon} alt={iconAlt} className={`mr-sm h-lg ${additionalClasses}`} />}
                {processingStatuses.includes(memberFile.statusId)
                    ? MemberFileStatusId[MemberFileStatusId.Processing]
                    : memberFile.statusId === MemberFileStatusId.Review ? (
                        <button
                            type="button"
                            className="flex max-h-xl max-w-[8rem] items-center justify-center font-sans-co text-link-blue underline-offset-2 hover:underline"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`review/${memberFile.id}`);
                            }}
                        >
                            Review
                        </button>
                    ) : (
                        MemberFileStatusId[memberFile.statusId]
                    )}
            </div>
        );
    }

    function navigateToDetails(memberFileId: number) {
        navigate(`details/${memberFileId}`);
    }

    return (
        <div>
            <div className="mb-xl">
                <h1 className={portalPageTitle}>Members</h1>
                <p className={`mt-sm ${portalPageSubtitle}`}>Add, remove, update scheme members</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div id="BulkMemberUploadContainer" className="mb-xl">
                    <MemberBulkUpload
                        onUpload={handleUpload}
                        setError={setError}
                        uploading={uploading}
                        disabled={uploadDisabled}
                        error={error}
                        isEmptyFile={isEmptyFile}
                        clearError={() => setError("")}
                    />
                </div>

                <div id="singleMemberUploadContainer" className="mb-xl">
                    <MemberSingleUpload disabled={uploadDisabled} />
                </div>
            </div>


            <Tabs
                activeTabKey={activeTabKey}
                onTabSelect={setActiveTabKey}
            >
                <Tab tabKey="1" name="Search Members"/>
                <Tab tabKey="2" name={secondTabTitle} />

                <TabContent associatedTabKey="1">
                    <div id="memberSearchContainer" className="w-full">
                        <MemberSearch />
                    </div>
                </TabContent>

                <TabContent associatedTabKey="2">
                    <div className={portalTableCard}>
                    <table id="memberFileTable" className="w-full">
                        <thead>
                        <tr className="h-2xl">
                            <th className={portalTableHeadCell}></th>
                            <th className={portalTableHeadCell}>
                                Name
                            </th>
                            <th className={portalTableHeadCell}>Status</th>
                            <th className={portalTableHeadCell}>New</th>
                            <th className={portalTableHeadCell}>Removed</th>
                            <th className={portalTableHeadCell}>Updated</th>
                            <th className={portalTableHeadCell}>
                                Unchanged
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {uploadedFiles.length > 0 &&
                            uploadedFiles
                                .slice((tablePage - 1) * paginationLimit, tablePage * paginationLimit)
                                .map((memberFile, index) => (
                                    <tr
                                        key={index}
                                        className={`h-2xl ${portalTableBodyRow}`}
                                    >
                                        <td className={`w-4xl ${portalTableBodyCell}`}>
                                            {new Date(memberFile.uploadedOn).toLocaleDateString()}
                                        </td>

                                        <td className={portalTableBodyCell}>
                                            <a
                                                className="hover:cursor-pointer text-link-blue"
                                                onClick={() => navigateToDetails(memberFile.id)}
                                            >
                                                {memberFile.filename === "single.csv" ? "Single upload" : memberFile.filename}
                                            </a>
                                        </td>
                                        <td className={portalTableBodyCell}>
                                            {getStatusElement(memberFile)}
                                        </td>
                                        <td className={`w-4xl ${portalTableBodyCell}`}>
                                            {memberFile.statusId === MemberFileStatusId.Confirmed && memberFile.new}
                                        </td>
                                        <td className={`w-4xl ${portalTableBodyCell}`}>
                                            {memberFile.statusId === MemberFileStatusId.Confirmed && memberFile.removed}
                                        </td>
                                        <td className={`w-4xl ${portalTableBodyCell}`}>
                                            {memberFile.statusId === MemberFileStatusId.Confirmed && memberFile.updated}
                                        </td>
                                        <td
                                            className={`w-4xl ${portalTableBodyCell}`}
                                        >
                                            {memberFile.statusId === MemberFileStatusId.Confirmed && memberFile.unchanged}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                    </div>

                    <div className="flex flex-row mt-lg ml-md">
                        {tablePage > 1 ? (
                            <a
                                className="hover:cursor-pointer text-mid-grey mr-md"
                                onClick={() => setTablePage(tablePage - 1)}
                            >
                                {"< Previous"}
                            </a>
                        ) : (
                            <p className="text-light-grey mr-md">{"< Previous"}</p>
                        )}
                        {tablePage < maxTablePage ? (
                            <a
                                className="hover:cursor-pointer text-mid-grey"
                                onClick={() => setTablePage(tablePage + 1)}
                            >
                                {"Next >"}
                            </a>
                        ) : (
                            <p className="text-light-grey">{"Next >"}</p>
                        )}
                    </div>
                </TabContent>
            </Tabs>
        </div>
    );
}

export default Members;
